import _                     from 'underscore';
import { v4 as uuid }        from 'uuid';
import React, { useReducer } from 'react';
import { produce }           from 'immer';
import { Uuid }              from '../types';

enum ACTIONS {
	ADD_TEST_INPUT     = 'addInput',
	ADD_TEST_RESULT    = 'addResult',
	UPDATE_TEST_INPUT  = 'updateInput',
	UPDATE_TEST_RESULT = 'updateResult',
	REMOVE_TEST_INPUT  = 'removeInput',
	REMOVE_TEST_RESULT = 'removeResult',
	SET_INPUTS         = 'setInputs',
	SET_RESULTS        = 'setResults',
	SET_PARAMS         = 'setParams',
	RESET_INPUTS       = 'resetInputs',
	RESET_RESULTS      = 'resetResults',
	RESET_ALL          = 'resetAll',
	UNDO               = 'undoAction',
	REDO               = 'redoAction',
}

interface UseInputTesterHookActionsInput {
	type: ACTIONS,
	value?: any,
	id?: Uuid,
	stateAttr?: 'inputs' | 'results'
}

interface UseInputTesterHookActions {
	addInput( value: any ): void,
	
	addResult( value: any ): void,
	
	updateInput( id: Uuid, value: any ): void,
	
	updateResult( id: Uuid, value: any ): void,
	
	removeInput( id: Uuid ): void,
	
	removeResult( id: Uuid ): void,
	
	setInputs( value: any[] ): void,
	
	setResults( value: any[] ): void,
	
	setParams( value: any ): void,
	
	resetInputs(): void,
	
	resetResults(): void,
	
	resetAll(): void,
}

export interface UseInputTesterHookState {
	params?: object,
	inputs?: any[],
	results?: any[]
}

export type TesterStateType<D> = UseInputTesterHookState & D;

export function useInputTester<D = {}>( initialState: D ): [ state: TesterStateType<D>, actions: UseInputTesterHookActions ]{
	
	let state = {
		params:  {},
		inputs:  [],
		results: [],
		...initialState,
	} as TesterStateType<D>
	
	let actionReducer = function reducer( state: UseInputTesterHookState, action: UseInputTesterHookActionsInput ){
		
		switch( action.type ){
			
			case ACTIONS.ADD_TEST_INPUT:
			case ACTIONS.ADD_TEST_RESULT:
				return produce( state, draft => {
					
					let newItem = { id: uuid(), ...action.value };
					
					draft[ action.stateAttr ].push( newItem )
				} );
			case ACTIONS.UPDATE_TEST_INPUT:
			case ACTIONS.UPDATE_TEST_RESULT:
				return produce( state, draft => {
					
					draft[ action.stateAttr ] = _.map( draft[ action.stateAttr ], item => {
						
						return ( item.id === action.id ) ? action.value : item;
					} );
					
				} );
			case ACTIONS.REMOVE_TEST_INPUT:
			case ACTIONS.REMOVE_TEST_RESULT:
				return produce( state, draft => {
					
					draft[ action.stateAttr ] = _.reject( draft[ action.stateAttr ], { id: action.id } );
					
				} );
			case ACTIONS.SET_INPUTS:
			case ACTIONS.SET_RESULTS:
				return produce( state, draft => {
					
					let items = _.map( action.value, item => ( { id: uuid(), ...item } ) );
					
					draft[ action.stateAttr ] = items;
					
				} );
			case ACTIONS.RESET_ALL:
				return produce( state, draft => {
					
					draft.inputs  = [];
					draft.results = [];
					
				} );
			
			case ACTIONS.SET_PARAMS:
				return produce( state, draft => {
					draft.params = { ...state.params, ...action.value };
					
				} );
			default:
				throw new Error();
		}
	};
	
	const [ _state, _dispatch ] = useReducer( actionReducer, state );
	
	return [
		_state as TesterStateType<D>,
		{
			[ ACTIONS.ADD_TEST_INPUT ]:     ( value = {} ) => _dispatch( { type: ACTIONS.ADD_TEST_INPUT, value, stateAttr: 'inputs' } ),
			[ ACTIONS.ADD_TEST_RESULT ]:    ( value = {} ) => _dispatch( { type: ACTIONS.ADD_TEST_RESULT, value, stateAttr: 'results' } ),
			[ ACTIONS.UPDATE_TEST_INPUT ]:  ( id = null, value = {} ) => _dispatch( { type: ACTIONS.UPDATE_TEST_INPUT, value, id, stateAttr: 'inputs' } ),
			[ ACTIONS.UPDATE_TEST_RESULT ]: ( id = null, value = {} ) => _dispatch( { type: ACTIONS.UPDATE_TEST_RESULT, value, id, stateAttr: 'results' } ),
			[ ACTIONS.REMOVE_TEST_INPUT ]:  ( id = null ) => _dispatch( { type: ACTIONS.REMOVE_TEST_INPUT, id, stateAttr: 'inputs' } ),
			[ ACTIONS.REMOVE_TEST_RESULT ]: ( id = null ) => _dispatch( { type: ACTIONS.REMOVE_TEST_RESULT, id, stateAttr: 'results' } ),
			[ ACTIONS.SET_INPUTS ]:         ( value = [] ) => _dispatch( { type: ACTIONS.SET_INPUTS, value, stateAttr: 'inputs' } ),
			[ ACTIONS.SET_RESULTS ]:        ( value = [] ) => _dispatch( { type: ACTIONS.SET_RESULTS, value, stateAttr: 'results' } ),
			[ ACTIONS.SET_PARAMS ]:         ( value = {} ) => _dispatch( { type: ACTIONS.SET_PARAMS, value } ),
			[ ACTIONS.RESET_INPUTS ]:       () => _dispatch( { type: ACTIONS.SET_INPUTS, value: [], stateAttr: 'inputs' } ),
			[ ACTIONS.RESET_RESULTS ]:      () => _dispatch( { type: ACTIONS.SET_RESULTS, value: [], stateAttr: 'results' } ),
			[ ACTIONS.RESET_ALL ]:          () => _dispatch( { type: ACTIONS.RESET_ALL } ),
		}
	];
}
